import { MobileHeader } from './MobileHeader';
import { cva, type VariantProps } from 'class-variance-authority';
import { DesktopHeader } from './DesktopHeader';
import { DesktopHeader as DesktopHeaderOld } from './desktop-old/DesktopHeaderOld';
import { DisplayMode } from '../../../types/enum/display-mode';
import type { SearchSuggestionResultGroup } from '../../search/view-models/search-suggestion';
import type { HeaderSearchTrackingConfig } from './HeaderSearch';
import type { CurrentCategoryType } from '../states/current-category-atom';

export const _headerVariants = cva(
    'duration-50 z-fixed w-full transition-all',
    {
        variants: {
            color: {
                default: 'bg-background text-primary', // default with dark text
                transparent: 'bg-transparent text-gray-50', // transparent with light text
                legacy: 'bg-[#FAF9F7]', // legacy background color
                none: 'bg-transparent text-primary', // transparent with dark text
                app: 'bg-white text-primary md:bg-[#F6F6F6]', // member app background color
            },
            mode: {
                default: 'relative',
                fixed: 'fixed md:absolute',
                float: 'absolute',
            },
        },
        defaultVariants: {
            color: 'default',
            mode: 'default',
        },
    }
);

interface HeaderCartItem {
    id: number;
    title: string;
    price: string;
    quantity: number;
    thumbnail: {
        desktop: string;
        mobile: string;
    };
    isSample: boolean;
}

export interface HeaderCartInfo {
    items: HeaderCartItem[];
    itemTotal: { ShortFormat: string; Amount: number };
    cartCount: number;
}

interface HeaderBaseProps extends VariantProps<typeof _headerVariants> {
    userData: HeaderUserData;
    cartInfo: HeaderCartInfo;
    currentUrl: string;
    onInboxClick?: () => void;
    onLogout: () => void;
    onLoginClick: () => void;
    onUserCartClick: () => void;
    warningMessages?: string[];
    onNavigate?: (url: string) => void;
    isLegacyBrokenRemMode?: boolean;
}

interface HeaderProps extends HeaderBaseProps {
    // Specific props for the main Header component
    currentCountryCode: string;
    countries: HeaderCountryElement[];
    primaryDesktopLinks: HeaderNavLinks;
    primaryMobileLinks: HeaderNavLinks;
    secondaryMobileLinks: HeaderNavLinks;
    profileMobileLinks: HeaderNavLinks;
    displayMode: DisplayMode;
    additionalUserMenuItems?: HeaderNavLinks;
    isSearching?: boolean;
    onMobileSearchClick?: () => void;
    uiUpdatesJune2025Enabled?: boolean;
    fetchSuggestions?: (query: string) => void;
    suggestionData?: SearchSuggestionResultGroup | null;
    headerSearchTrackingConfig?: HeaderSearchTrackingConfig;
    showTextLogoOnly?: boolean;
}

export interface HeaderCountryElement {
    code: string;
    name: string;
    url: string;
}

export interface HeaderCompanyInbox {
    id: number;
    count: number;
    inboxType?: string;
}

export interface HeaderUserData {
    isAuthorized: boolean;
    avatarSrc?: string;
    firstName: string;
    lastName?: string;
    notificationsCount: number;
    professionals: HeaderUserDataProfessional[];
    /** Pre-grouped professional sections ordered by country */
    professionalSections: ProfessionalSection[];
    /** Whether to show country labels (true when multiple countries exist) */
    showCountryLabels: boolean;
    inboxCount: number;
    designBoardCount: number;
    orderNotificationCount: number;
    websiteNotificationCount: number;
}

/**
 * Represents a professional profile's essential data for display throughout the website.
 * This interface was exported to enable consistent professional representation across multiple components:
 * - Desktop user dropdown menu (professional account switcher in header)
 * - Mobile navigation menu (professional account switcher)
 * - Member dashboard sidebar navigation (logged-in homepage professional list)
 */
export interface HeaderUserDataProfessional {
    id: number;
    title: string;
    logo: string;
    companyLogo48Resized?: string;
    companyLogo96Resized?: string;
    logoBackgroundColor?: string;
    urlSegment: string;
    unreads: number;
    country?: string;
}

/**
 * Represents a section of professionals grouped by country
 */
export interface ProfessionalSection {
    country: string;
    label: string;
    professionals: HeaderUserDataProfessional[];
}

export interface HeaderNavLinks {
    title?: string;
    links: Array<{
        title: string;
        to: string;
        badgeCount?: number;
        onClick?: () => void;
        external?: boolean;
        isNonRemixRoute?: boolean;
        isSeparator?: boolean;
        prefix?: string;
        category?: CurrentCategoryType;
    }>;
}

export const Header = (props: HeaderProps) => {
    const {
        // Base props
        color,
        userData,
        currentUrl,
        onInboxClick,
        onLogout,
        onLoginClick,
        onUserCartClick,
        warningMessages,
        isLegacyBrokenRemMode,

        // Specific props
        mode,
        currentCountryCode,
        countries,
        primaryDesktopLinks,
        primaryMobileLinks,
        secondaryMobileLinks,
        profileMobileLinks,
        displayMode,
        additionalUserMenuItems,
        isSearching,
        onMobileSearchClick,
        cartInfo,
        uiUpdatesJune2025Enabled,
        onNavigate,
        fetchSuggestions,
        suggestionData,
        headerSearchTrackingConfig,
        showTextLogoOnly,
    } = props;

    // Choose which desktop header to use based on the feature flag
    const DesktopHeaderComponent = uiUpdatesJune2025Enabled
        ? DesktopHeader
        : DesktopHeaderOld;

    return displayMode === DisplayMode.Desktop ? (
        <DesktopHeaderComponent
            // Base props
            color={color}
            userData={userData}
            currentUrl={currentUrl}
            onInboxClick={onInboxClick}
            onLogout={onLogout}
            onLoginClick={onLoginClick}
            onUserCartClick={onUserCartClick}
            warningMessages={warningMessages}
            isLegacyBrokenRemMode={isLegacyBrokenRemMode}
            // Specific props
            primaryLinks={primaryDesktopLinks}
            currentCountryCode={currentCountryCode}
            countries={countries}
            additionalUserMenuItems={additionalUserMenuItems}
            cartInfo={cartInfo}
            isSearching={isSearching}
            uiUpdatesJune2025Enabled={uiUpdatesJune2025Enabled}
            onNavigate={onNavigate}
            fetchSuggestions={fetchSuggestions}
            suggestionData={suggestionData}
            headerSearchTrackingConfig={headerSearchTrackingConfig}
            showTextLogoOnly={showTextLogoOnly}
        />
    ) : (
        <MobileHeader
            // Base props
            color={color}
            userData={userData}
            currentUrl={currentUrl}
            onLogout={onLogout}
            onLoginClick={onLoginClick}
            onUserCartClick={onUserCartClick}
            warningMessages={warningMessages}
            isLegacyBrokenRemMode={isLegacyBrokenRemMode}
            // Specific props
            mode={mode}
            primaryLinks={primaryMobileLinks}
            secondaryLinks={secondaryMobileLinks}
            profileLinks={profileMobileLinks}
            onMobileSearchClick={onMobileSearchClick}
            cartInfo={cartInfo}
            currentCountryCode={currentCountryCode}
        />
    );
};
